<template>
  <div>
    <h3>{{$t('message.os_update') | title}}</h3>

    <div v-if="willRunOnEachClusterNode" class="cluster-info">
      {{ $t('message.will_run_on_each_cluster_node') | title }}
    </div>
    <div v-if="willRunOnThisClusterNodeOnly" class="cluster-info">
      {{ $t('message.will_run_on_this_cluster_node_only') | title }}
    </div>

    <b-form enctype="multipart/form-data">
      <b-form-row>
        <label class="label label-default col-3">{{$t('message.script_update_os_update_file_source') | title}}</label>
        <b-col>
          <b-form-select v-model="fileSource"
            :options="fileSourceOptions" @change="uploadFile=''; publicFile=''"/>
          </b-col>
      </b-form-row>
      <b-form-row v-if="fileSource == 'upload'">
        <label class="label label-default col-3">{{$t('message.script_update_os_update_upload_file') | title}}</label>
        <b-col cols="9">
          <b-form-file v-model="uploadFile" :state="Boolean(uploadFile)" :placeholder="$t('message.file_upload_placeholder')"
            @change="clearState">
          </b-form-file>
        </b-col>
      </b-form-row>
      <b-form-row v-if="fileSource == 'public'">
        <label class="label label-default col-3">{{$t('message.script_update_os_update_public_file') | title}}</label>
        <b-col cols="9">
          <b-form-select v-model="publicFile" :options="publicFileOptions"
            :class="{'file-selector-ok': publicFile && publicFile.value, 'file-selector-nok': !publicFile || !publicFile.value}"/>
        </b-col>
      </b-form-row>
      <b-form-row>
        <label class="label label-default col-3">{{$t('message.comment') | title}}</label>
        <b-col cols="9">
          <b-form-textarea v-model="comment" :rows="3" :required="false" />
        </b-col>
      </b-form-row>

      <b-button type="button" class="padmin-button submit-button"
        @click="update" :disabled="saveDisabled" variant="primary">
        <b-spinner v-if="updateInProgress" small type="grow"/>
        {{$t('message.button_update') | title}}
      </b-button>

      <LastUpdateInfo ref="lastUpdate"/>
    </b-form>

    <b-alert v-model="showError" variant="danger" dismissible @dismissed="error=''; showError=false;">
      <div> {{ error }} </div>
      <div v-if="errorRemark"> {{ errorRemark }}</div>
    </b-alert>

    <ConsoleOutput v-if="updateInitiated" :result="result" :errorCode="errorCode" :output="output"
      :uploadPercent="uploadPercent"/>

  </div>
</template>

<script>
import ConsoleOutput from "./ConsoleOutput"
import { get, getFreeDiskSpace, post } from "../http"
import LastUpdateInfo from "./LastUpdateInfo"

export default {
  name: "OsUpdate",

  components: {
    ConsoleOutput, LastUpdateInfo
  },

  data() {
    return {
      fileSource: 'upload',
      uploadFile: null,
      publicFile: null,
      needsReboot: false,
      rebooting: false,
      result: null,
      errorCode: null,
      output: "",
      updateInProgress: false,
      updateInitiated: false,
      error: null,
      errorRemark: null,
      showError: false,
      uploadPercent: 0,
      willRunOnEachClusterNode: false,
      willRunOnThisClusterNodeOnly: false,
      addComment: false,
      comment: ''
    }
  },

  beforeRouteLeave (to, from , next) {
    if (this.needsReboot) {
      this.$root.$emit("bv::show::modal", "reboot-local-modal-id")
    } else {
      next()
    }
  },

  created() {
    window.addEventListener('beforeunload', this.beforeWindowUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
  },

  mounted() {
    get('/update_bundle').then(resp => {
      this.willRunOnEachClusterNode = resp.data.will_run_on_each_cluster_node
      this.willRunOnThisClusterNodeOnly = resp.data.will_run_on_this_cluster_node_only
      this.addComment = resp.data.add_comment
      this.publicFileOptions = resp.data.public_file_options
    }, err => {
      console(err)
    })
  },

  computed: {
    fileSourceOptions() {
      return [{value: 'upload', text: this.$options.filters.title(this.$t('message.script_update_upload'))},
       {value: 'public', text: this.$options.filters.title(this.$t('message.script_update_public'))}]
    },

    saveDisabled() {
      return (this.fileSource == 'upload' && !this.uploadFile) ||
      (this.fileSource == 'public' && !this.publicFile) || this.updateInProgress
    }
  },

  methods: {
    clearState() {
      this.result = null
      this.output = ''
      this.error = null
      this.errorCode = null
      this.showError = false
      this.errorRemark = null
      this.comment = ''
    },

    loadProgress (commandId) {
      get('/command_progress/' + commandId).then(resp => {
        this.output = resp.data.output
        this.result = resp.data.result
        this.updateInProgress = resp.data.in_progress
        if (this.updateInProgress || this.result == undefined || this.result == null) {
          setTimeout(() => {
            this.loadProgress(commandId)
          }, 1000)
        } else {
          this.errorCode = resp.data.error_code
          if (this.result) {
            this.needsReboot = true
            this.$root.$emit("bv::show::modal", "reboot-local-modal-id")
          }
        }
      })
    },

    async update() {
      this.error = ''
      this.showError = false
      this.result = null
      this.output = ''
      this.uploadPercent = 0

      const formData = new FormData()
      formData.append('file_source', this.fileSource)

      if (this.fileSource == 'upload') {
        if(!this.uploadFile || !this.uploadFile.name.toLowerCase().includes('passos')){
          this.error = this.$t('message.error_invalid_file_format')
          this.showError = true
          return
        }

        const freeDiskSpaceResp = await getFreeDiskSpace()
        if (freeDiskSpaceResp.error) {
          this.error = freeDiskSpaceResp.error
          this.showError = true
          return
        }
        const fileSize = this.uploadFile.size
        const neededSpace = fileSize * 3
        if(neededSpace > freeDiskSpaceResp.free_disk_space) {
          const needMore = Math.ceil((neededSpace - freeDiskSpaceResp.free_disk_space) / 1024 / 1024)
          this.error = this.$t('message.error_not_enough_disk_space') + needMore + ' M'
          this.showError = true
          return
        }
        formData.append('upload_file', this.uploadFile)
      } else {
        formData.append('public_file', this.publicFile)
      }

      this.updateInitiated = true
      this.updateInProgress = true
      if (this.addComment) {
        formData.append('comment', this.comment)
      }
      post('/update_bundle', formData, {'content-type': 'multipart/form-data'}, this.onUploadProgress).then(resp => {
        this.result = resp.data.result
        this.errorCode = resp.data.error_code
        if (resp.data.result) {
          // async save on backend, so check progress for response
          if (resp.data.command_id) {
              this.loadProgress(resp.data.command_id)
          }
        } else {
          this.updateInProgress = false
          this.output = resp.data.output
          this.$refs.lastUpdate.getLastRun()
        }
        if (resp.data.error) {
          this.showError = true
          this.error = this.$t('message.error_' + resp.data.error)
        }
      }, err => {
        this.result = false
        this.updateInProgress = false
        this.error = err
        this.showError = true
      })
    },

    onUploadProgress(progressEvent) {
      this.uploadPercent = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100))
    }
  }
}
</script>
<style scoped>
label {
  margin: 0 25px 0 0 !important;
}

.custom-file, .custom-file-input{
  width: 85%;
}

.alert {
  margin: 10px;
}
</style>
